// Unified Event Tracking Utility for SportsGuru247
// Ensures ALL events have: event_type, match_name, cta_name, device_type, page_url, timestamp

// ============= DEVICE DETECTION =============

export const detectDevice = () => {
  if (typeof window === 'undefined') return 'desktop';
  return window.innerWidth <= 768 ? 'mobile' : 'desktop';
};

// ============= MATCH NAME EXTRACTION =============

export const extractMatchName = () => {
  // Try to extract from H1 heading first
  const h1 = document.querySelector('h1');
  if (h1) {
    const h1Text = h1.innerText.trim();
    // Check if it looks like a match name (contains "vs" or "VS")
    if (h1Text.includes(' vs ') || h1Text.includes(' VS ')) {
      return h1Text;
    }
  }
  
  // Try to extract from page title
  const pageTitle = document.title;
  if (pageTitle) {
    // Extract match name pattern: "Team A vs Team B"
    const vsMatch = pageTitle.match(/([^-|]+\s+vs\s+[^-|]+)/i);
    if (vsMatch) {
      return vsMatch[1].trim();
    }
  }
  
  // Try to extract from URL slug
  const pathname = window.location.pathname;
  if (pathname.includes('/predictions/')) {
    // URL format: /predictions/football-xxx or /predictions/ipl-xxx
    const slug = pathname.split('/predictions/')[1];
    if (slug) {
      // Try to get match info from data attributes on the page
      const matchCard = document.querySelector('[data-match-name]');
      if (matchCard) {
        return matchCard.getAttribute('data-match-name');
      }
    }
  }
  
  // Check for IPL page
  if (pathname.includes('/ipl')) {
    return 'IPL 2026 - Cricket Predictions';
  }
  
  // Fallback to cleaned page title
  if (pageTitle) {
    // Remove site name suffix
    return pageTitle.replace(/\s*[-|]\s*SportsGuru247.*$/i, '').trim() || 'Homepage';
  }
  
  return 'SportsGuru247 Homepage';
};

// ============= CTA NAME MAPPING =============

const CTA_NAMES = {
  // Main CTAs
  'view-best-odds-top': 'main_top',
  'view-best-odds-bottom': 'final_cta',
  'view-prediction': 'mid_content',
  'view-ipl-prediction': 'mid_content',
  'view-picks-btn': 'main_top',
  
  // Banners
  'top-banner': 'sticky_desktop',
  'mid-page-banner': 'mid_content',
  'sticky-footer-banner': 'sticky_mobile',
  'inline-banner': 'mid_content',
  
  // Telegram/Popups (for future use)
  'exit-popup': 'tg_exit_popup',
  'scroll-popup': 'tg_scroll_popup',
  'telegram-bar': 'tg_sticky_bar',
  
  // Navigation
  'nav-home': 'nav_home',
  'nav-ipl': 'nav_ipl',
  'nav-about': 'nav_about',
  'nav-contact': 'nav_contact',
};

export const getCTAName = (elementId, elementText = '') => {
  // Direct mapping
  if (CTA_NAMES[elementId]) {
    return CTA_NAMES[elementId];
  }
  
  // Pattern matching
  if (elementId) {
    if (elementId.includes('view-best-odds')) return 'main_top';
    if (elementId.includes('view-prediction')) return 'mid_content';
    if (elementId.includes('banner')) return 'mid_content';
    if (elementId.includes('funbet')) return 'operator_cta';
  }
  
  // Text-based fallback
  if (elementText) {
    const lowerText = elementText.toLowerCase();
    if (lowerText.includes('view best odds')) return 'main_top';
    if (lowerText.includes('view full prediction')) return 'mid_content';
    if (lowerText.includes('full analysis')) return 'mid_content';
    if (lowerText.includes('start betting')) return 'operator_cta';
    if (lowerText.includes("today's picks")) return 'main_top';
  }
  
  // Device-aware fallback for unknown CTAs
  const device = detectDevice();
  return device === 'mobile' ? 'sticky_mobile' : 'sticky_desktop';
};

// ============= PAGE CATEGORY =============

export const getPageCategory = () => {
  const pathname = window.location.pathname;
  if (pathname.includes('/ipl') || pathname.includes('/cricket')) return 'cricket';
  if (pathname.includes('/football') || pathname.includes('/predictions')) return 'football';
  if (pathname.includes('/casino')) return 'casino';
  if (pathname === '/') return 'home';
  return 'other';
};

// ============= UNIFIED EVENT STRUCTURE =============

export const createEventPayload = (eventType, ctaName, customMatchName = null) => {
  const matchName = customMatchName || extractMatchName();
  const deviceType = detectDevice();
  
  // Validate - no undefined or empty values
  const payload = {
    event_type: eventType || 'view',
    match_name: matchName || 'SportsGuru247',
    cta_name: ctaName || 'unknown_cta',
    device_type: deviceType,
    page_url: window.location.href,
    page_category: getPageCategory(),
    referrer: document.referrer || 'direct',
    utm_source: new URLSearchParams(window.location.search).get('utm_source') || null,
    timestamp: new Date().toISOString(),
  };
  
  // Debug logging
  console.log('TRACK EVENT:', {
    type: payload.event_type,
    match: payload.match_name,
    cta: payload.cta_name,
    device_type: payload.device_type,
  });
  
  return payload;
};

// ============= MAIN TRACK FUNCTION =============

// Store for sent events (prevent duplicates)
const sentEvents = new Set();

export const trackEvent = (type, match, cta, options = {}) => {
  const payload = createEventPayload(type, cta, match);
  
  // Add any additional options
  if (options.metadata) {
    payload.metadata = options.metadata;
  }
  
  // Create unique key for deduplication (within 1 second)
  const eventKey = `${type}-${cta}-${Math.floor(Date.now() / 1000)}`;
  if (sentEvents.has(eventKey) && !options.allowDuplicate) {
    console.debug('TRACK EVENT: Skipped duplicate', eventKey);
    return payload;
  }
  sentEvents.add(eventKey);
  
  // Clean up old keys after 5 seconds
  setTimeout(() => sentEvents.delete(eventKey), 5000);
  
  // Send to all tracking services
  sendToGA4(payload);
  sendToReddit(payload);
  sendToMongoDB(payload);
  
  return payload;
};

// ============= GA4 TRACKING =============

const sendToGA4 = (payload) => {
  if (typeof window.gtag !== 'function') return;
  
  try {
    window.gtag('event', payload.event_type, {
      match_name: payload.match_name,
      cta_name: payload.cta_name,
      device_type: payload.device_type,
      page_category: payload.page_category,
    });
  } catch (e) {
    console.debug('GA4 tracking error:', e);
  }
};

// ============= REDDIT PIXEL TRACKING =============

const sendToReddit = (payload) => {
  if (typeof window.rdt !== 'function') return;
  
  try {
    // Map event types to Reddit events
    let redditEvent = 'Custom';
    if (payload.event_type === 'view') {
      redditEvent = 'PageVisit';
    } else if (payload.event_type === 'click') {
      if (payload.cta_name.includes('operator') || payload.cta_name.includes('funbet')) {
        redditEvent = 'Lead';
      } else {
        redditEvent = 'ViewContent';
      }
    }
    
    window.rdt('track', redditEvent, {
      customEventName: `${payload.event_type}_${payload.cta_name}`,
      value: payload.match_name,
      itemCount: 1,
    });
  } catch (e) {
    console.debug('Reddit tracking error:', e);
  }
};

// ============= MONGODB TRACKING =============

const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'sportsguru247.com' || 
        hostname.includes('deploy.emergentcf.cloud') ||
        hostname.includes('sportsguru')) {
      return '/api';
    }
  }
  return process.env.REACT_APP_BACKEND_URL 
    ? `${process.env.REACT_APP_BACKEND_URL}/api` 
    : '/api';
};

const sendToMongoDB = async (payload) => {
  try {
    const API = getApiUrl();
    await fetch(`${API}/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: payload.event_type,
        page: payload.page_url,
        element_id: payload.cta_name,
        element_text: payload.match_name,
        metadata: {
          device_type: payload.device_type,
          page_category: payload.page_category,
          referrer: payload.referrer,
          utm_source: payload.utm_source,
          timestamp: payload.timestamp,
          // Preserve caller-supplied metadata (A/B variant, slide_index, match_id…)
          ...(payload.metadata || {}),
        }
      })
    });
  } catch (e) {
    console.debug('MongoDB tracking error:', e);
  }
};

// ============= CONVENIENCE FUNCTIONS =============

// Track page view
export const trackPageView = (matchName = null) => {
  return trackEvent('view', matchName, 'page_view');
};

// Track CTA click
export const trackCTAClick = (ctaId, ctaText = '', matchName = null) => {
  const ctaName = getCTAName(ctaId, ctaText);
  return trackEvent('click', matchName, ctaName);
};

// Track prediction view
export const trackPredictionView = (matchName) => {
  return trackEvent('view', matchName, 'prediction_detail');
};

// Track banner click
export const trackBannerClick = (bannerId, bannerSlot) => {
  const ctaName = getCTAName(bannerId) || bannerSlot || 'banner_click';
  return trackEvent('click', null, ctaName);
};

// Track outbound click (FunBet, operators)
export const trackOutboundClick = (destination, ctaId = 'operator_cta') => {
  const matchName = extractMatchName();
  const ctaName = destination.includes('funbet') ? 'funbet_click' : getCTAName(ctaId);
  return trackEvent('click', matchName, ctaName, {
    metadata: { destination_url: destination }
  });
};

// ============= CLICK LISTENER =============

let clickListenerActive = false;

export const initUnifiedClickTracking = () => {
  if (clickListenerActive) return;
  clickListenerActive = true;
  
  document.addEventListener('click', (e) => {
    const target = e.target.closest('a, button, [data-track], [data-testid]');
    if (!target) return;
    
    const href = target.getAttribute('href') || '';
    const text = target.innerText?.substring(0, 100) || '';
    const testId = target.getAttribute('data-testid') || '';
    const trackId = target.getAttribute('data-track') || '';
    
    // Skip if no identifiable element
    if (!href && !testId && !trackId) return;
    
    // Get CTA name
    const ctaId = trackId || testId;
    const ctaName = getCTAName(ctaId, text);
    
    // Get match name from context
    const matchName = extractMatchName();
    
    // Determine if outbound
    const isOutbound = href.startsWith('http') && !href.includes(window.location.hostname);
    const isFunBet = href.includes('funbet');
    
    if (isFunBet) {
      trackEvent('click', matchName, 'funbet_click', {
        metadata: { destination_url: href }
      });
    } else if (isOutbound) {
      trackEvent('click', matchName, 'operator_cta', {
        metadata: { destination_url: href }
      });
    } else {
      trackEvent('click', matchName, ctaName);
    }
  });
  
  console.log('[Unified Tracking] Click listener initialized');
};

// ============= SCROLL & ENGAGEMENT TRACKING =============

let scrollTracked = false;
let engagedTracked = false;

export const initEngagementTracking = () => {
  // 45 second engagement
  setTimeout(() => {
    if (!engagedTracked) {
      engagedTracked = true;
      trackEvent('view', extractMatchName(), 'engaged_45sec');
    }
  }, 45000);
  
  // Scroll tracking
  const handleScroll = () => {
    if (scrollTracked) return;
    
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
    
    if (scrollPercent >= 60) {
      scrollTracked = true;
      trackEvent('view', extractMatchName(), 'scroll_60');
    }
  };
  
  let scrollTimeout = null;
  window.addEventListener('scroll', () => {
    if (!scrollTimeout) {
      scrollTimeout = setTimeout(() => {
        scrollTimeout = null;
        handleScroll();
      }, 100);
    }
  }, { passive: true });
};

// Reset for SPA navigation
export const resetEngagementTracking = () => {
  scrollTracked = false;
  engagedTracked = false;
};

// ============= EXPORTS =============

export default {
  trackEvent,
  trackPageView,
  trackCTAClick,
  trackPredictionView,
  trackBannerClick,
  trackOutboundClick,
  initUnifiedClickTracking,
  initEngagementTracking,
  resetEngagementTracking,
  detectDevice,
  extractMatchName,
  getCTAName,
  getPageCategory,
};
